/**
 * Schema types for screen template content definitions.
 *
 * Schemas describe, declaratively, which YAML fields from a screen template
 * contribute to each named audio/transcript slot — without needing the DOM.
 */

/** Which pipeline(s) a slot is used for. Defaults to 'all'. */
export type SlotUse = 'audio' | 'transcript' | 'all';

/**
 * A single field contribution within a slot.
 * `field` is a dot-path into the screen object (or iterated item object).
 */
export interface SlotPart {
  /** Dot-path of the field to read (e.g. 'heading', 'feedback.correct'). */
  field: string;
  /**
   * Alternative field paths tried in order when the primary field is empty.
   * (e.g. ['subheading', 'title'] as fallbacks for 'heading')
   */
  fallback?: string[];
  /**
   * Omit this part entirely if any of the listed sibling fields are non-empty.
   * Useful to avoid repeating e.g. a label when a heading is already present.
   */
  omitIfPresent?: string[];
}

/**
 * A slot part that iterates over a child array on the current object and folds
 * all the collected text into the *containing* slot (not a separate sub-slot).
 *
 * Use this when the audio for a screen should include content from its child
 * items inline — e.g. a cards screen whose intro reads out each card's heading
 * and text. This is distinct from `IteratedScreenSlot`, which produces separate
 * named sub-slots (`card-0`, `card-1` …) with individual audio files.
 *
 * `parts` is recursive — you can nest another `InlineIteratedPart` to handle
 * a second level (e.g. `templates[]` inside each card). In practice, keep the
 * nesting shallow: reading the full content of deeply nested screens in the
 * intro audio makes it very long and unhelpful.
 */
export interface InlineIteratedPart {
  /** Dot-path of the array field to iterate over (e.g. 'cards', 'templates'). */
  iterateField: string;
  /**
   * When items lack `id`, translation keys use
   * `${hostScreenId}.${itemKeyPath}.${index}.${field}`.
   */
  itemKeyPath?: string;
  /** Parts to extract from each item in the array (can include further InlineIteratedParts). */
  parts: AnySlotPart[];
}

/** Union of all part types accepted in a slot's parts list. */
export type AnySlotPart = SlotPart | InlineIteratedPart;

/** Type guard — true when part iterates over a child array. */
export function isInlineIteratedPart(
  part: AnySlotPart
): part is InlineIteratedPart {
  return 'iterateField' in part;
}

/** A named slot for a fixed (non-iterated) section of screen content. */
export interface ScreenSlot {
  use?: SlotUse;
  parts: AnySlotPart[];
  /** S3/JiT marker id; `${id}` is replaced with the screen id. */
  markerKey?: string;
}

/**
 * A slot that iterates over an array field on the screen (e.g. tabs, accordions)
 * and produces one sub-slot per item: `${keyPrefix}-0`, `${keyPrefix}-1`, etc.
 */
export interface IteratedScreenSlot {
  use?: SlotUse;
  /** Dot-path of the array field on the screen (e.g. 'tabs', 'items'). */
  iterate: string;
  /** Slot name prefix — item at index 2 becomes `${keyPrefix}-2`. */
  keyPrefix: string;
  /**
   * When true, the S3 audio key for each item is derived from the item's own
   * `id` field rather than from the parent screen's `id`.
   *
   * Use this when the template's `<audio-marker>` names follow the pattern
   * `${item.id}-${keyPrefix}-${index}` instead of `${screen.id}-${keyPrefix}-${index}`.
   * Example: `screen-accordion.vue` uses `${card.id}-accordion-${i}`.
   */
  useItemId?: boolean;
  /** Per-item marker; `${id}` and `${index}` are substituted. */
  markerKey?: string;
  /**
   * Parts extracted from each item. May include `InlineIteratedPart`s to fold a
   * nested child array (e.g. `templates[]` inside a tab/accordion/blind item)
   * into the item's own clip — the same recursive mechanism `intro` slots use.
   */
  parts: AnySlotPart[];
}

export function isIteratedSlot(
  slot: ScreenSlot | IteratedScreenSlot
): slot is IteratedScreenSlot {
  return 'iterate' in slot;
}

/** Full schema definition for a screen template. */
export interface ScreenSchema {
  /**
   * Template name(s) this schema applies to.
   * A schema can cover multiple template aliases.
   */
  template: string | string[];
  /**
   * Named slots, keyed by slot name (e.g. 'intro', 'feedback.correct', 'tab').
   * The 'intro' slot is played automatically when the section becomes active.
   */
  slots: Record<string, ScreenSlot | IteratedScreenSlot>;
  /**
   * When true, the generic `uc-screen.vue` sectionActive intro watcher is
   * suppressed and the template component is responsible for the full audio
   * sequence (intro + any queued follow-on audio) in its own watcher.
   *
   * Use this for templates that need to queue a second audio clip after the
   * intro, e.g. tabs (intro → queue active tab) or slides (intro → queue
   * active slide). Without this flag both the generic watcher and the
   * template's watcher would call play() independently, causing a race.
   */
  managesOwnSectionAudio?: boolean;
  /**
   * When `'lightbox'`, the `intro` slot uses S3/JiT key `lightbox-${screen.id}`
   * (matches `<audio-marker :name="\`lightbox-${id}\`"/>` in `uc-lightbox.vue`),
   * not bare `screen.id`.
   */
  introKeyMode?: 'lightbox';
  /**
   * When set, the `intro` slot uses S3/JiT key `${screen.id}${introKeySuffix}`
   * instead of bare `screen.id` (e.g. hotspot-fullscreen instructions).
   */
  introKeySuffix?: string;
  /**
   * Declares which slots are warmed on section entry (lookahead / bulk pre-seed).
   * When omitted, {@link resolveJitEntryTargets} falls back to template-specific rules.
   */
  entryPrefetch?: EntryPrefetchKind;
}

export type EntryPrefetchKind =
  | 'intro'
  | 'intro-and-initial-tab'
  | 'split-chain'
  | 'sticky-chain'
  | 'templates-chain'
  | 'course-title'
  | 'unit-title'
  | 'timeline-intro'
  | 'carousel-intro-and-slide-0'
  | 'blinds-intro-and-initial'
  | 'flipcards-intro'
  | 'accordion-intro'
  | 'slides-first-child-intro'
  | 'hotspot-game-entry';
